import {Size} from './zoomPan';
import {NumberedRevision} from './revisions';

/** A rectangle on a picture, as fractions (0–1) of its displayed width and height, from the top left. */
export interface AnchorRect {
    x: number;
    y: number;
    w: number;
    h: number;
}

/** Smaller drags are taken for a slip of the mouse rather than a selection. */
const MIN_FRACTION = 0.01;

const clamp01 = (v: number) => Math.min(Math.max(v, 0), 1);

/** The rectangle spanned by two corners (fractions, possibly off the picture), clipped to it; null when too small. */
export function rectFromCorners(a: { x: number; y: number }, b: { x: number; y: number }): AnchorRect | null {
    const x1 = clamp01(Math.min(a.x, b.x));
    const y1 = clamp01(Math.min(a.y, b.y));
    const x2 = clamp01(Math.max(a.x, b.x));
    const y2 = clamp01(Math.max(a.y, b.y));
    if (x2 - x1 < MIN_FRACTION || y2 - y1 < MIN_FRACTION) return null;
    return {x: x1, y: y1, w: x2 - x1, h: y2 - y1};
}

/**
 * A Drive comment anchor (an opaque JSON string to Drive) for a rectangle on a picture, in the format
 * Drive's own viewer writes for image comments: `[null, [null, [left, top, right, bottom]], null, revisionId]`,
 * the corners as fractions of the picture, of the version `revisionId` (the current one: the last of
 * the file's revisions).
 */
export function formatRectAnchor(rect: AnchorRect, revisionId: string | null): string {
    return JSON.stringify([null, [null, [rect.x, rect.y, rect.x + rect.w, rect.y + rect.h]], null, revisionId]);
}

const isNumber = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

/** A region of a picture a comment is anchored to, on the version of the picture it was drawn on. */
export interface CommentAnchor {
    rect: AnchorRect;
    /** The revision of the file it was drawn on; null when not recorded. */
    revisionId: string | null;
}

/** A comment's anchor, or null when it has none or it isn't a rectangle (e.g. set by another app). */
export function parseCommentAnchor(anchor: string | undefined): CommentAnchor | null {
    if (!anchor) return null;
    let parsed: unknown;
    try {
        parsed = JSON.parse(anchor);
    } catch {
        return null;
    }
    const corners = Array.isArray(parsed) && Array.isArray(parsed[1]) ? parsed[1][1] : undefined;
    if (!Array.isArray(corners) || corners.length < 4) return null;
    const [left, top, right, bottom] = corners;
    if (!isNumber(left) || !isNumber(top) || !isNumber(right) || !isNumber(bottom)) return null;
    const rect = rectFromCorners({x: left, y: top}, {x: right, y: bottom});
    if (!rect) return null;
    const revisionId = (parsed as unknown[])[3];
    return {rect, revisionId: typeof revisionId === 'string' && revisionId ? revisionId : null};
}

/**
 * Which version of the file an anchor's region was drawn on, against its versions (`numberRevisions`):
 * the current one (also when unknown: not recorded, or the versions aren't loaded), an older one still
 * kept, or one Drive no longer keeps (it drops old versions after a while), necessarily older.
 */
export type AnchorVersion =
    | { kind: 'current' }
    | { kind: 'older'; revision: NumberedRevision }
    | { kind: 'gone' };

export function anchorVersion(revisionId: string | null, revisions: NumberedRevision[]): AnchorVersion {
    if (!revisionId || revisions.length === 0) return {kind: 'current'};
    const found = revisions.find((r) => r.revision.id === revisionId);
    if (!found) return {kind: 'gone'};
    return found.latest ? {kind: 'current'} : {kind: 'older', revision: found};
}

/** The picture's aspect ratio inside the rectangle (width / height), for a cropped preview of it. */
export function rectAspectRatio(rect: AnchorRect, size: Size | null): number {
    const ratio = size && size.height > 0 ? size.width / size.height : 1;
    return (rect.w / rect.h) * ratio;
}
